import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { searchIndex } from '../services/searchIndex';
import { SearchIndexResponse, SearchResult } from '../types/search';

/**
 * Fetch the search index from the API
 */
async function fetchSearchIndex(): Promise<SearchIndexResponse> {
  return api.get<SearchIndexResponse>('/search/index');
}

/**
 * Return type for useSearch hook
 */
interface UseSearchReturn {
  /** Search function that queries the index */
  search: (query: string) => SearchResult[];
  /** Whether the search index is ready */
  isIndexReady: boolean;
  /** Whether the index is currently loading */
  isLoading: boolean;
  /** Error if index fetch failed */
  error: Error | null;
  /** Refresh the search index */
  refreshIndex: () => void;
}

/**
 * Hook for searching tasks and pages using Fuse.js
 *
 * @example
 * ```tsx
 * const { search, isIndexReady, isLoading } = useSearch();
 *
 * // Search when ready
 * const results = isIndexReady ? search('security audit') : [];
 * ```
 */
export function useSearch(): UseSearchReturn {
  const [isIndexReady, setIsIndexReady] = useState(false);
  const indexBuiltRef = useRef(false);

  // Fetch search index data
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<SearchIndexResponse, Error>({
    queryKey: ['searchIndex'],
    queryFn: fetchSearchIndex,
    // Keep the index fresh but don't refetch too often
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
  });

  // Build the index when data is available
  useEffect(() => {
    if (data?.data && !indexBuiltRef.current) {
      searchIndex.buildIndex(data.data);
      setIsIndexReady(true);
      indexBuiltRef.current = true;
    }
  }, [data]);

  // Rebuild index when data changes
  useEffect(() => {
    if (data?.data && indexBuiltRef.current) {
      searchIndex.buildIndex(data.data);
    }
  }, [data]);

  // Search function
  const search = useCallback((query: string): SearchResult[] => {
    if (!isIndexReady || !query.trim()) {
      return [];
    }
    return searchIndex.search(query);
  }, [isIndexReady]);

  // Refresh function
  const refreshIndex = useCallback(() => {
    indexBuiltRef.current = false;
    setIsIndexReady(false);
    refetch();
  }, [refetch]);

  return {
    search,
    isIndexReady,
    isLoading,
    error: error ?? null,
    refreshIndex,
  };
}

/**
 * Hook for debounced search
 *
 * @example
 * ```tsx
 * const { query, setQuery, results, isSearching } = useDebouncedSearch(300);
 *
 * return (
 *   <input value={query} onChange={(e) => setQuery(e.target.value)} />
 * );
 * ```
 */
export function useDebouncedSearch(debounceMs: number = 300): {
  query: string;
  setQuery: (q: string) => void;
  results: SearchResult[];
  isSearching: boolean;
  isIndexReady: boolean;
  isLoading: boolean;
} {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { search, isIndexReady, isLoading } = useSearch();

  // Debounce the query
  useEffect(() => {
    if (!query.trim()) {
      setDebouncedQuery('');
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      setIsSearching(false);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Execute search when debounced query changes
  const results = debouncedQuery ? search(debouncedQuery) : [];

  return {
    query,
    setQuery,
    results,
    isSearching,
    isIndexReady,
    isLoading,
  };
}

export default useSearch;
